import * as React from 'react'
import type {
  BaseOptionType,
  DefaultOptionType,
  LabeledValue,
} from 'antd/es/select'

import type { RawValueType, SelectFilterOption } from './types'

/* -------------------------------------------------------------------------
 * Option normalisation for the shadcn/cmdk half of the `src/antd/Select` seam.
 *
 * Every rule here is a TRANSCRIPTION of rc-select's own behaviour — rc-select
 * 14.x, the version antd 5.15.4 ships and this repo pins — so the shadcn
 * branch filters, labels and groups options exactly as the antd branch beside
 * it does:
 *
 *   • rc-select/es/utils/valueUtil.ts        → fillFieldNames / flattenOptions
 *   • rc-select/es/hooks/useFilterOptions.ts → the default filter
 *   • rc-select/es/Select.tsx                → tag-mode option filling
 *
 * Where a rule looks odd (see `includesSearch`) it is odd in antd too. Parity
 * beats "better" here: 280 files were written against these rules, and a
 * filter that starts matching MORE than it used to is a behaviour change, not
 * a repaint.
 *
 * Ported from the `update-design` reference branch (`src/antd/Select/
 * options.tsx`) with one substantive change: THE `<Select.Option>` MARKERS ARE
 * NOT REDECLARED HERE. The reference branch deletes antd, so it had to ship
 * its own placeholder components. On this branch `Select.Option` is still
 * rc-select's own, and rc-select already sets the two flags this module reads
 * (`isSelectOption` / `isSelectOptGroup`) — so one marker serves BOTH design
 * modes and a call site's `<Select.Option>` cannot end up bound to the wrong
 * one. That is why this file is a `.ts` and not the reference's `.tsx`.
 *
 * A leaf module: it imports React for `Children.forEach` and antd's option
 * TYPES, and nothing else.
 * ------------------------------------------------------------------------- */

export const fillFieldNames = (
  fieldNames: SelectFieldNames | undefined,
  childrenAsData: boolean
) => {
  const { label, value, options, groupLabel } = fieldNames || {}
  const mergedLabel = label || (childrenAsData ? 'children' : 'label')
  return {
    label: mergedLabel,
    value: value || 'value',
    options: options || 'options',
    groupLabel: groupLabel || mergedLabel,
  }
}

export type SelectFieldNames = {
  value?: string
  label?: string
  groupLabel?: string
  options?: string
}

export type MergedFieldNames = ReturnType<typeof fillFieldNames>

const isOptGroupElement = (node: React.ReactElement) =>
  Boolean((node.type as { isSelectOptGroup?: boolean })?.isSelectOptGroup)

/**
 * `<Select.Option>` / `<Select.OptGroup>` children → the `options` array shape.
 *
 * Mirrors rc-select's `convertChildrenToData`: the element's own `children`
 * become the option's `children` FIELD, which `fillFieldNames` then uses as the
 * label — that is what "childrenAsData" means. 16 `<Select.Option>` tags in
 * this repo depend on it.
 */
export const convertChildrenToOptions = (
  children: React.ReactNode
): DefaultOptionType[] => {
  const out: DefaultOptionType[] = []
  React.Children.forEach(children, (node) => {
    if (!React.isValidElement(node)) return
    const props = { ...(node.props as Record<string, unknown>) }
    const { children: nodeChildren, ...rest } = props
    if (isOptGroupElement(node)) {
      out.push({
        ...rest,
        options: convertChildrenToOptions(nodeChildren as React.ReactNode),
      } as DefaultOptionType)
      return
    }
    out.push({
      ...rest,
      // rc-select accepts the element's `key` as a stand-in for `value`.
      value:
        (rest.value as RawValueType | undefined) ??
        (node.key != null ? String(node.key) : undefined),
      children: nodeChildren,
    } as DefaultOptionType)
  })
  return out
}

/** rc-select's `toArray` — NOT `React.Children.toArray`. */
const toArray = <T>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value]

/**
 * rc-select's default match test, verbatim:
 * `toArray(test).join('').toUpperCase().includes(search)`.
 *
 * Note the consequence, which antd has too: when a label is a React element
 * (a Transifex `<T/>`, say), `join('')` yields `[object Object]`, so default
 * filtering never matches it. The call sites that need real search on a
 * translated label already pass `optionFilterProp` pointing at a string field,
 * or their own `filterOption` — 77 and 49 of them respectively.
 */
const includesSearch = (test: unknown, upperSearch: string) =>
  toArray(test).join('').toUpperCase().includes(upperSearch)

/**
 * rc-select's `injectPropsWithOption`: a custom `filterOption` is handed an
 * object that answers `option.props.x` as well as `option.x`, because antd v3
 * passed the `<Option>` ELEMENT and some call sites still read `.props`.
 */
const injectPropsWithOption = <T extends BaseOptionType>(option: T): T => {
  if (!option || 'props' in option) return option
  const next = { ...option }
  Object.defineProperty(next, 'props', {
    get: () => next,
    enumerable: false,
    configurable: true,
  })
  return next
}

export type FilterArgs = {
  searchValue: string
  filterOption: SelectFilterOption
  optionFilterProp?: string
  fieldNames: MergedFieldNames
}

/** Transcription of rc-select's `useFilterOptions`. */
export const filterRawOptions = (
  options: DefaultOptionType[],
  { searchValue, filterOption, optionFilterProp, fieldNames }: FilterArgs
): DefaultOptionType[] => {
  if (!searchValue || filterOption === false) return options

  const fieldOptions = fieldNames.options
  const fieldLabel = fieldNames.label
  const fieldValue = fieldNames.value
  const customizeFilter = typeof filterOption === 'function'
  const upperSearch = searchValue.toUpperCase()

  const filterFunc = customizeFilter
    ? (filterOption as (input: string, option?: DefaultOptionType) => boolean)
    : (_input: string, option?: DefaultOptionType) => {
        if (!option) return false
        if (optionFilterProp) {
          return includesSearch(option[optionFilterProp], upperSearch)
        }
        if (option[fieldOptions]) {
          // An OptGroup's children live under `children`, not `label`, so
          // rc-select hard-codes the fallback to `label` for a group row.
          return includesSearch(
            option[fieldLabel !== 'children' ? fieldLabel : 'label'],
            upperSearch
          )
        }
        return includesSearch(option[fieldValue], upperSearch)
      }

  const wrap = customizeFilter
    ? injectPropsWithOption
    : (opt: DefaultOptionType) => opt

  const filtered: DefaultOptionType[] = []
  options.forEach((item) => {
    if (item[fieldOptions]) {
      if (filterFunc(searchValue, wrap(item))) {
        filtered.push(item)
        return
      }
      const subOptions = (item[fieldOptions] as DefaultOptionType[]).filter(
        (sub) => filterFunc(searchValue, wrap(sub))
      )
      if (subOptions.length) {
        filtered.push({ ...item, [fieldOptions]: subOptions })
      }
      return
    }
    if (filterFunc(searchValue, wrap(item))) filtered.push(item)
  })
  return filtered
}

export type NormalizedOption = {
  key: string
  value: RawValueType
  label: React.ReactNode
  disabled: boolean
  title?: string
  data: DefaultOptionType
  index: number
}

export type NormalizedGroup = {
  key: string
  label: React.ReactNode
  data: DefaultOptionType
  options: NormalizedOption[]
}

export type NormalizedEntry = NormalizedGroup | NormalizedOption

export const isGroupEntry = (
  entry: NormalizedEntry
): entry is NormalizedGroup => Array.isArray((entry as NormalizedGroup).options)

const toOption = (
  data: DefaultOptionType,
  fieldNames: MergedFieldNames,
  index: number
): NormalizedOption => {
  const value = data[fieldNames.value] as RawValueType
  let key: string
  if (data.key != null) key = String(data.key)
  else if (value !== undefined && value !== null) key = String(value)
  else key = `rc-index-key-${index}`
  return {
    key,
    value,
    label: data[fieldNames.label] as React.ReactNode,
    disabled: Boolean(data.disabled),
    title: data.title,
    data,
    index,
  }
}

/**
 * Flatten a (possibly grouped) option array into the render entries plus the
 * flat option list the keyboard and selection code walks.
 */
export const normalizeOptions = (
  options: DefaultOptionType[],
  fieldNames: MergedFieldNames
): { entries: NormalizedEntry[]; flat: NormalizedOption[] } => {
  const entries: NormalizedEntry[] = []
  const flat: NormalizedOption[] = []
  options.forEach((data, index) => {
    const groupChildren = data[fieldNames.options] as
      | DefaultOptionType[]
      | undefined
    if (groupChildren) {
      const groupOptions = groupChildren.map((child, childIndex) =>
        toOption(child, fieldNames, flat.length + childIndex)
      )
      groupOptions.forEach((option) => flat.push(option))
      entries.push({
        key: data.key != null ? String(data.key) : `group-${index}`,
        label: data[fieldNames.groupLabel] as React.ReactNode,
        data,
        options: groupOptions,
      })
      return
    }
    const option = toOption(data, fieldNames, flat.length)
    flat.push(option)
    entries.push(option)
  })
  return { entries, flat }
}

/** rc-select's `createTagOption`. */
export const createTagOption = (
  value: RawValueType,
  label: React.ReactNode,
  fieldNames: MergedFieldNames
): DefaultOptionType =>
  ({
    [fieldNames.value]: value,
    [fieldNames.label]: label ?? value,
  } as DefaultOptionType)

const isRawValue = (value: unknown): value is RawValueType =>
  typeof value !== 'object' || value === null

/** A resolved selection: antd's `LabeledValue` plus the option's own flags. */
export type DisplayValue = LabeledValue & { disabled?: boolean; title?: string }

/**
 * rc-select's `convert2LabelValues` — normalise whatever the caller passed as
 * `value` (raw, an array, `{ value, label }` under `labelInValue`) into
 * labelled values, filling a missing label from the option map and then from
 * the label cache.
 */
export const toLabeledValues = (
  value: unknown,
  {
    multiple,
    valueOptions,
    labelCache,
    fieldNames,
    optionLabelProp,
  }: {
    multiple: boolean
    valueOptions: Map<RawValueType, DefaultOptionType>
    labelCache: Map<RawValueType, LabeledValue>
    fieldNames: MergedFieldNames
    optionLabelProp?: string
  }
): DisplayValue[] => {
  let list: unknown[]
  if (value === undefined || value === null) list = []
  else if (Array.isArray(value)) list = value
  else list = [value]

  const labeled = list
    .filter((val) => val !== undefined && val !== null)
    .map((val) => {
      let rawValue: RawValueType
      let rawLabel: React.ReactNode
      let rawKey: string | undefined
      let rawDisabled: boolean | undefined
      let rawTitle: string | undefined

      if (isRawValue(val)) {
        rawValue = val
      } else {
        const labeledVal = val as LabeledValue
        rawKey = labeledVal.key
        rawLabel = labeledVal.label
        rawValue = labeledVal.value ?? (labeledVal.key as RawValueType)
      }

      const option = valueOptions.get(rawValue)
      if (option) {
        if (rawLabel === undefined) {
          rawLabel = option[optionLabelProp || fieldNames.label]
        }
        if (rawKey === undefined) {
          rawKey = option.key != null ? String(option.key) : String(rawValue)
        }
        rawDisabled = option.disabled
        rawTitle = option.title
      } else if (rawLabel === undefined) {
        // THE LABEL CACHE. An already-selected option can drop out of
        // `options` when an async search replaces the list. antd keeps the
        // resolved label (rc-select's `useCache`) so the control does not flip
        // back to showing a raw id — `AsyncSelectWithFetchMore` depends on it.
        rawLabel = labelCache.get(rawValue)?.label
      }

      return {
        key: rawKey,
        value: rawValue,
        label: rawLabel,
        disabled: rawDisabled,
        title: rawTitle,
      } as DisplayValue
    })

  if (!multiple && labeled.length > 1) return labeled.slice(0, 1)
  return labeled
}
